import React, { useCallback, useEffect, useState } from "react";
import LanguageContext from "../../helper/LanguageContext";
import { callAPI } from "../../helper/apiUtils";
import { $crud } from "../../helper/CrudFactory";
import ModernToggleSwitch from "../ModernToggleSwitch";
import InfoTip from "../InfoTip";
import "./MediaAutoSyncSettings.css";

export default function MediaAutoSyncSettings({
  scopeId,
  kind,
  targetId,
  accent,
  isInternal,
  emailTheme,
}) {
  const { strings } = React.useContext(LanguageContext);
  const [state, setState] = useState(null);
  const [folderPath, setFolderPath] = useState("");
  const [busy, setBusy] = useState(false);
  const variant = emailTheme ? "violet" : isInternal ? "green" : "orange";
  const scopeType = kind === "building" ? "building" : "group";

  const load = useCallback(async () => {
    if (!targetId) return;
    try {
      const { data } = await callAPI(
        "/media-export-target",
        { scopeType, scopeId: targetId },
        "GET"
      );
      if (data && data.status) {
        setState(data);
        setFolderPath(data.folderPath || "");
      }
    } catch (e) {
      setState({ connected: false, canEdit: false });
    }
  }, [scopeType, targetId]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (next) => {
    if (!targetId || busy) return;
    setBusy(true);
    try {
      const { data } = await callAPI(
        "/media-export-target",
        {},
        "PUT",
        {
          scopeType,
          scopeId: targetId,
          enabled: !!next.enabled,
          folderPath: next.folderPath,
        }
      );
      if (data && data.status) {
        setState(data);
        setFolderPath(data.folderPath || next.folderPath || "");
      } else {
        await $crud.notify({
          type: "error",
          message: (data && data.message) || strings.something_wrong || "Could not save",
        });
        load();
      }
    } catch (e) {
      load();
    } finally {
      setBusy(false);
    }
  };

  const handleToggle = async () => {
    if (!state?.canEdit || busy) return;
    if (!state.enabled && !folderPath.trim()) return;
    await save({ enabled: !state.enabled, folderPath: folderPath.trim() });
  };

  const handlePathBlur = async () => {
    if (!state?.canEdit || busy) return;
    const next = folderPath.trim();
    if (next === (state.folderPath || "")) return;
    if (state.enabled && !next) return;
    await save({ enabled: !!state.enabled && !!next, folderPath: next });
  };

  if (!targetId || !state || state.kjuupPlus === false || state.mediaExportEnabled === false) return null;

  // Hidden for now — keep the component so it can be re-enabled later.
  return null;

  const connected = !!state?.connected;
  const canEdit = !!state?.canEdit;
  const enabled = !!state?.enabled;
  const path = (state?.folderPath || "").trim();

  return (
    <div className="media-auto-sync">
      <div className="media-auto-sync-row">
        <span className="material-symbols-outlined media-auto-sync-icon media-auto-sync-settings__icon" style={{ "--media-auto-sync-accent": accent }}>
          folder_managed
        </span>
        <div className="media-auto-sync-copy">
          <div className="media-auto-sync-title">
            {strings.media_export_folder || "Nightly media export"}
            <InfoTip
              title={strings.media_export_folder || "Nightly media export"}
              text={
                strings.media_export_nightly_info ||
                "New images, videos, and documents are uploaded every evening at 20:00 to the folder path on the company file server. The app does not need to be open."
              }
            />
          </div>
          {!connected ? (
            <div className="media-auto-sync-subtitle">
              {strings.media_export_admin_required ||
                "A file server must be connected in the Admin Panel."}
            </div>
          ) : canEdit ? (
            <>
              <div className="media-auto-sync-subtitle">
                {strings.media_export_nightly_hint ||
                  "New images, videos, and documents are uploaded at 20:00"}
              </div>
              <input
                className="media-auto-sync-path"
                type="text"
                value={folderPath}
                placeholder={strings.media_export_folder_placeholder || "/Projects/Alpha"}
                disabled={busy}
                onChange={(e) => setFolderPath(e.target.value)}
                onBlur={handlePathBlur}
              />
              {state?.lastError ? (
                <div className="media-auto-sync-error">
                  {strings.media_export_last_error || "Last export failed"}: {state.lastError}
                </div>
              ) : null}
            </>
          ) : (
            <div className="media-auto-sync-subtitle">
              {enabled && path
                ? (strings.media_export_readonly || "Exports nightly to {path}").replace(
                    "{path}",
                    path
                  )
                : strings.media_export_nightly_hint ||
                  "New images, videos, and documents are uploaded at 20:00"}
            </div>
          )}
        </div>
        {connected && canEdit ? (
          <ModernToggleSwitch
            checked={enabled}
            onChange={handleToggle}
            id={`media-export-${scopeId || targetId}`}
            disabled={busy || (!enabled && !folderPath.trim())}
            variant={variant}
          />
        ) : null}
      </div>
    </div>
  );
}
